import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Lock, Check, X } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

/**
 * Shuffle an array using Fisher-Yates.
 */
function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const CategoriesForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const categories = content?.categories || [];

    // Flatten all items with their source category (non-empty only), shuffled once per categories ref
    const allItems = useMemo(() => {
        const flat = [];
        categories.forEach((cat) => {
            (cat.items || []).forEach((item) => {
                const s = String(item || '').trim();
                if (s) flat.push({ item: s, category: cat.name });
            });
        });
        return shuffle(flat);
    }, [categories]);

    // Which item is currently selected (by index in allItems)
    const [selectedIdx, setSelectedIdx] = useState(null);
    // Assignments: { [itemName]: categoryName }
    const [assignments, setAssignments] = useState(() => (userAnswer?.assignments && typeof userAnswer.assignments === 'object' ? { ...userAnswer.assignments } : {}));

    // Guards the sync-from-userAnswer effect below against clobbering in-progress, unsubmitted
    // edits when a socket reconnect re-fetches the activity and hands back a freshly-parsed
    // (but content-identical) userAnswer object. Flips true the moment the participant makes a
    // local change since their last actual submit; reset to false right when a submit is fired
    // (local state now IS the submitted state) or when userAnswer goes back to null (fresh
    // activity, nothing to protect).
    const hasLocalEditsRef = useRef(false);

    useEffect(() => {
        if (hasLocalEditsRef.current) return;
        if (userAnswer?.assignments && typeof userAnswer.assignments === 'object') {
            setAssignments({ ...userAnswer.assignments });
        } else if (userAnswer == null) setAssignments({});
    }, [userAnswer]);

    const handleItemClick = (idx) => {
        if (optionsDisabled) return;
        setSelectedIdx(idx === selectedIdx ? null : idx);
    };

    const handleCategoryClick = (catName) => {
        if (optionsDisabled || selectedIdx === null) return;
        const itemName = allItems[selectedIdx].item;
        hasLocalEditsRef.current = true;
        setAssignments(prev => ({ ...prev, [itemName]: catName }));
        setSelectedIdx(null);
    };

    const removeAssignment = (itemName) => {
        if (optionsDisabled) return;
        hasLocalEditsRef.current = true;
        setAssignments(prev => {
            const next = { ...prev };
            delete next[itemName];
            return next;
        });
    };

    const assignedCount = allItems.filter(({ item }) => assignments[item]).length;
    const allAssigned = assignedCount === allItems.length && allItems.length > 0;

    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const submittedAssignments = userAnswer?.assignments && typeof userAnswer.assignments === 'object' ? userAnswer.assignments : {};
    const isUnchanged = hasSubmitted && allItems.length > 0 && allItems.every(({ item }) => assignments[item] === submittedAssignments[item]);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || !allAssigned || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    const unassigned = allItems.filter(({ item }) => !assignments[item]);

    const submitPayload = () => {
        const out = {};
        allItems.forEach(({ item }) => { if (assignments[item]) out[item] = assignments[item]; });
        return { assignments: out };
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Assign items to their categories'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>

            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}

            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}

            {allItems.length === 0 ? (
                <p className={styles.description} style={{ marginBottom: 0 }}>
                    No items to assign. The host has not added any items to categories yet.
                </p>
            ) : (
                <>
            <div className={styles.hint} style={{ marginBottom: 'var(--space-1)' }}>
                Tap an item, then tap a category to assign it. Assign all items to submit.
            </div>

            {/* Unassigned items pool */}
            {unassigned.length > 0 && (
                <div className={styles.categoryPool} style={{ marginBottom: 'var(--space-3)' }}>
                    {allItems.map(({ item }, idx) => {
                        if (assignments[item]) return null;
                        const isSelected = idx === selectedIdx;
                        return (
                            <button
                                key={`${item}-${idx}`}
                                type="button"
                                className={`${styles.categoryChip} ${isSelected ? styles.selected : ''}`}
                                onClick={() => handleItemClick(idx)}
                                disabled={optionsDisabled}
                                aria-pressed={isSelected}
                            >
                                {item}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Category drop zones */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {categories.map((cat) => {
                    const catItems = allItems.filter(({ item }) => assignments[item] === cat.name);
                    const isTarget = selectedIdx !== null;
                    return (
                        <div
                            key={cat.name}
                            role={isTarget && !optionsDisabled ? 'button' : undefined}
                            tabIndex={isTarget && !optionsDisabled ? 0 : undefined}
                            onClick={() => handleCategoryClick(cat.name)}
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && isTarget && !optionsDisabled) {
                                    e.preventDefault();
                                    handleCategoryClick(cat.name);
                                }
                            }}
                            className={`${styles.categoryDropZone} ${isTarget ? styles.active : ''}`}
                            aria-label={`${cat.name} category${isTarget ? ', tap to place selected item here' : ''}`}
                        >
                            {/* Category label */}
                            <div className={styles.categoryName} style={{ marginBottom: catItems.length > 0 ? 'var(--space-2)' : 0 }}>
                                {cat.name}
                            </div>

                            {/* Assigned items */}
                            {catItems.length > 0 && (
                                <div className={styles.categoryAssignedItems}>
                                    {catItems.map(({ item }) => (
                                        <button
                                            key={item}
                                            type="button"
                                            className={styles.categoryAssignedChip}
                                            onClick={(e) => { e.stopPropagation(); removeAssignment(item); }}
                                            disabled={optionsDisabled}
                                            aria-label={`Remove ${item} from ${cat.name}`}
                                        >
                                            {item} {!optionsDisabled && <X size={12} aria-hidden />}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Empty placeholder */}
                            {catItems.length === 0 && (
                                <div className={styles.categoryEmptyHint}>
                                    {isTarget ? 'Tap to place here' : 'No items assigned'}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Submit */}
            <button
                className={styles.submitBtn}
                onClick={() => {
                    if (optionsDisabled) return;
                    hasLocalEditsRef.current = false;
                    onSubmit(submitPayload());
                }}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
                </>
            )}
        </div>
    );
};

export default CategoriesForm;
